// @flow
import { colors } from 'bench-style-constants';
import { MessageListContainer, messageListTypes } from 'bk-web-app/message-list';
import { Sidebar, SidebarSection } from 'bench-internal-theme';
import { formatDate, toDate } from 'date-functions';
import React from 'react';
import styled from 'styled-components';
import { FeatureValue, useFeatureFlags } from '@bench/ff-client';
// $FlowFixMe - TS file
import { SplitFeatureFlags } from '../features';

import DetailItem from './DetailItem';
import EditLedgerDetailsContainer from './EditLedgerDetailsContainer';
import type { LedgerAccount } from './types';

const MetadataSection = styled.div`
  color: ${colors.cloud};
  border-top: 1px solid ${colors.silt};
  margin-top: 16px;
  padding: 12px 16px;
`;

const MetadataItem = styled.div`
  font-size: 11px;
  line-height: 1.4;
  margin: 4px 0;
`;

const MetadataLabel = styled.span`
  font-weight: 600;
`;

type LedgerAccountSidebarProps = {
  dismiss: () => void,
  ledgerAccount: LedgerAccount,
};

const yearMonthFormatOptions = {
  year: 'numeric',
  month: 'numeric',
  timeZone: 'UTC',
};

const LedgerAccountSidebar = ({ dismiss, ledgerAccount }: LedgerAccountSidebarProps) => {
  const [featureState] = useFeatureFlags();
  const {
    featureContext: { features },
  } = featureState;

  const isAssignCategoryImprovementEnabled =
    features[SplitFeatureFlags.ASSIGN_CATEGORY_IMPROVEMENT]?.value === FeatureValue.FEATURE_ON;

  const autoPostEnabled = ledgerAccount.autoPostEnabled ? 'Enabled' : 'Disabled';

  // v2 has lastCompletedMonth at root level, v1 has it nested in accountSettings
  const lastCompletedMonthValue = isAssignCategoryImprovementEnabled
    ? ledgerAccount.lastCompletedMonth
    : ledgerAccount.accountSettings?.lastCompletedMonth;

  const lastCompletedMonth = lastCompletedMonthValue
    ? formatDate(toDate(lastCompletedMonthValue), yearMonthFormatOptions)
    : 'N/A';

  const statementDayOfMonth = ledgerAccount.statementDayOfMonth
    ? ledgerAccount.statementDayOfMonth.toString()
    : 'N/A';

  return (
    <Sidebar dismiss={dismiss} headerContent={ledgerAccount.name}>
      <SidebarSection hasBorderBottom title="Details">
        <EditLedgerDetailsContainer
          details={ledgerAccount.details}
          ledgerAccountId={ledgerAccount.id}
        />
      </SidebarSection>
      <SidebarSection hasBorderBottom={!!ledgerAccount.role} title="Internal Notes">
        <MessageListContainer
          listType={messageListTypes.LEDGER_ACCOUNT_INTERNAL_NOTE}
          messages={ledgerAccount.internalNotes}
          parentId={ledgerAccount.id}
          placeholder="Leave a note for your team"
          zeroStateMessage="(Currently no internal notes for ledger account.)"
        />
      </SidebarSection>
      {ledgerAccount.role && ledgerAccount.accountSettings && (
        <SidebarSection background={colors.pearl} title="Functional Account Ledger">
          <DetailItem label="Auto-Posting" value={autoPostEnabled} />
          <DetailItem label="Last Completed Month" value={lastCompletedMonth} />
          <DetailItem label="Statement Date" value={statementDayOfMonth} />
        </SidebarSection>
      )}
      <MetadataSection>
        <MetadataItem>
          <MetadataLabel>Ledger Account ID:</MetadataLabel> {ledgerAccount.id}
        </MetadataItem>
      </MetadataSection>
    </Sidebar>
  );
};

export default LedgerAccountSidebar;
